/**
 * PLの1行(表示用モデル)。現行「PL日報」の「1日」シートの行に対応する。
 * PlPage(集計) と PlSheetView / PlTreeView(描画) の両方から使うため独立させている。
 */
import { formatYen } from '@core/lib/money';

export type Fmt = 'int' | 'yen' | 'pct' | 'price' | 'ratio';

/** PL表の1行。values は会社ID(または期間キー) → 値(null=空欄)。total は合計列 */
export type PlRow = {
  key: string;
  label: string;
  fmt: Fmt;
  values: Record<string, number | null>;
  total: number | null;
  /** 行の意味(カーソルで吹き出し) */
  hint?: string;
  better?: string;
  bold?: boolean;
  section?: boolean; // 区分の見出し行
  children?: PlRow[];
  level?: number;
  /** 合計列に別の表示(粗利率など) */
  totalExtra?: string;
  /** 原価率の帯色を付ける */
  colorRate?: boolean;
  /** 親階層の色分け(PL階層版。子・孫にも引き継ぐ。2026-09-10 指示) */
  tone?: string;
  /** 小計・合計・率の行(①シート版では行まるごと大分類の色。2026-09-11 指示の見本) */
  summary?: boolean;
};

/**
 * 科目のまとまり(行の色分け)。①シート版と②階層版で同じ科目に同じ色を使う(2026-09-11 指示)。
 * appt=アポ数 / sales=売上 / cost=売上原価 / gross=粗利 / exp=経費 / op=営業利益 / nonop=営業外経費 / ord=経常利益
 * 色は base.css の `.pl-table tr.pl-t-*`
 */
export const TONE_LABELS: Record<string, string> = {
  appt: 'アポ数',
  sales: '売上',
  cost: '売上原価',
  gross: '粗利',
  exp: '経費',
  op: '営業利益',
  nonsales: '営業外売上',
  nonop: '営業外経費',
  ord: '経常利益',
};

export function toneOf(key: string): string {
  if (key === 'ordinary') return 'ord';
  if (key === 'op_profit') return 'op';
  if (key.startsWith('sec_nonop') || key.startsWith('mgr_bonus') || key.startsWith('nonop')) return 'nonop';
  if (key.startsWith('sec_nonsales') || key.startsWith('nonsales')) return 'nonsales';
  if (key.startsWith('sec_exp') || key.startsWith('exp_')) return 'exp'; // expected(売上見込)は売上側なので exp_ で見る
  if (key.startsWith('gross')) return 'gross';
  if (key.startsWith('sec_cost') || key.startsWith('cost')) return 'cost';
  if (key.startsWith('sec_sales') || key.startsWith('sales') || key === 'unit' || key === 'expected' || key === 'cancel') return 'sales';
  return 'appt'; // 構成比・アポ数(目標/取得数)・訪問数
}

/** 金額の単位(PLの表示切替。2026-09-10 指示) */
export type MoneyUnit = 'yen' | 'k' | 'man';
export const MONEY_UNIT_LABELS: Record<MoneyUnit, string> = { yen: '円', k: '千円', man: '万円' };

/** 金額を単位付きで表示する。件数・率はそのまま */
export function fmtCellUnit(v: number | null, fmt: Fmt, unit: MoneyUnit): string {
  if (unit === 'yen' || (fmt !== 'yen' && fmt !== 'price')) return fmtCell(v, fmt);
  if (v === null || v === undefined || !Number.isFinite(v)) return '';
  const div = unit === 'k' ? 1000 : 10000;
  const x = v / div;
  const abs = Math.abs(x);
  const s = abs >= 100 ? Math.round(abs).toLocaleString('ja-JP') : abs.toFixed(1);
  return x < 0 ? `(${s})` : s;
}

export function fmtCell(v0: number | null, fmt: Fmt): string {
  if (v0 === null || v0 === undefined || !Number.isFinite(v0)) return '';
  const v = v0 + 0; // -0 を 0 に
  const r = Math.round(v) + 0;
  switch (fmt) {
    case 'int':
      return formatYen(r);
    case 'yen':
      return r < 0 ? `(${formatYen(-r)})` : formatYen(r);
    case 'price':
      return formatYen(r);
    case 'pct':
      return `${v.toFixed(2)}%`;
    case 'ratio':
      return `${v.toFixed(1)}%`;
  }
}

/** 展開できる行のキー(すべて開く/閉じる用) */
export function collectKeys(rows: PlRow[]): string[] {
  return rows.flatMap((r) => [...((r.children?.length ?? 0) > 0 ? [r.key] : []), ...collectKeys(r.children ?? [])]);
}
